import { DateText } from '@plato/ui-legacy/components/common/date-time-text';
import { Badge } from '@plato/ui-legacy/components/list-views/badge';
import { DataTable } from '@plato/ui-legacy/components/list-views/data-table';
import { TabContentTableWrapper } from '@plato/ui-legacy/components/tables/tab-content-table-wrapper';
import { EmptyText } from '@plato/ui-legacy/components/typography/empty-text';
import { AttachmentIcon } from '@plato/ui-legacy/components/ui/attachment-icon';
import type { ColumnDef } from '@tanstack/react-table';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrderAttachment } from '../hooks/use-order-attachments.tsx';
import { useOrderAttachments } from '../hooks/use-order-attachments.tsx';
import { AttachmentDownloadButton } from './attachment-download-button.tsx';
import { AttachmentPreviewButton } from './attachment-preview-button.tsx';

type AttachmentsTableProps = {
    transactionId: string;
};

export function QuoteOrderAttachmentsTab({ transactionId }: AttachmentsTableProps) {
    const { t } = useTranslation('transaction-details');

    // Fetch attachments data - only runs when component is mounted (tab is open)
    const { data: attachments = [], isLoading } = useOrderAttachments({ transactionId });

    const columns: ColumnDef<OrderAttachment>[] = useMemo(
        () => [
            {
                accessorKey: 'documentName',
                header: t('attachments__column_document'),
                cell: ({ row }) => (
                    <div className="flex items-center gap-4">
                        <AttachmentIcon fileType="PDF" />
                        <span className="text-sm text-neutral-900">{row.original.documentName}</span>
                    </div>
                ),
                size: 350,
            },
            {
                accessorKey: 'printedAt',
                header: t('attachments__column_date_added'),
                cell: ({ row }) =>
                    row.original.printedAt ? <DateText date={row.original.printedAt} /> : <EmptyText />,
                size: 130,
            },
            {
                accessorKey: 'documentTypeText',
                header: t('attachments__column_type'),
                cell: ({ row }) => (
                    <div className="flex">
                        <Badge color="neutral">{row.original.formularName}</Badge>
                    </div>
                ),
                size: 174,
            },
            {
                id: 'actions',
                header: '',
                cell: ({ row }) => (
                    <div className="flex items-center gap-3">
                        <AttachmentPreviewButton attachment={row.original} />
                        <AttachmentDownloadButton attachment={row.original} />
                    </div>
                ),
                size: 104,
            },
        ],
        [t],
    );

    const table = useReactTable({
        data: attachments,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <TabContentTableWrapper>
            <DataTable isLoading={isLoading} table={table} columns={columns} />
        </TabContentTableWrapper>
    );
}
